/**
 * URL Builder for Entity-to-Page Navigation Maps ICS entity types to their
 * corresponding page URLs and routing config.
 */

import { IAP_DASHBOARD_MODES } from '../components/IAP/ICSConstants';
import { getEntityTypeLabel as getEntityDisplayLabel } from '../components/DisasterGPT/incidentEntityComponents';

const { FUTURE } = IAP_DASHBOARD_MODES;

/** Routing configuration for each entity type */
interface RoutingConfig {
  phase: number;
  widget: string;
  tab: string;
}

/**
 * Default routing configs by entity type (keys match backend entity type
 * identifiers)
 */
const ENTITY_ROUTING: Record<string, RoutingConfig> = {
  // Objectives/Info -> Phase 1, Incident Objectives widget, objectives-actions tab
  objective: {
    phase: 1,
    widget: 'incidentObjectives',
    tab: 'objectives-actions',
  },
  incident_metadata: {
    phase: 1,
    widget: 'incidentObjectives',
    tab: 'objectives-actions',
  },
  incident_info: {
    phase: 1,
    widget: 'incidentObjectives',
    tab: 'objectives-actions',
  },
  // Actions -> Phase 3, Open Actions Tracker
  action: { phase: 3, widget: 'openActionsTracker', tab: 'objectives-actions' },
  // Strategies/Tactics -> Phase 3, Work Analysis Matrix. Both live on the 234,
  // which the combined 234 + 215 card used to embed.
  strategy: { phase: 3, widget: 'workAnalysisMatrix', tab: 'overview' },
  tactic: { phase: 3, widget: 'workAnalysisMatrix', tab: 'overview' },
  // Work Assignments -> Phase 5, Assignment List
  work_assignment: { phase: 5, widget: 'assignmentList', tab: 'overview' },
  // Resources -> Phase 1, Incident Status Summary
  resource_set: { phase: 1, widget: 'incidentStatusSummary', tab: 'resources' },
  incident_resource: {
    phase: 1,
    widget: 'incidentStatusSummary',
    tab: 'resources',
  },
  resource_in_incident: {
    phase: 1,
    widget: 'incidentStatusSummary',
    tab: 'resources',
  },
  strike_team: { phase: 1, widget: 'incidentStatusSummary', tab: 'resources' },
  resource_request: {
    phase: 1,
    widget: 'incidentStatusSummary',
    tab: 'resources',
  },
  // Medical -> Phase 7, Medical Plan
  hospital: { phase: 7, widget: 'medicalPlan', tab: 'overview' },
  medical_transportation: { phase: 7, widget: 'medicalPlan', tab: 'overview' },
  medical_aid_station: { phase: 7, widget: 'medicalPlan', tab: 'overview' },
  // Communications -> Phase 7, Radio Communications Plan
  coms: {
    phase: 7,
    widget: 'incidentRadioCommunicationsPlan',
    tab: 'overview',
  },
  // Calendar -> Phase 0, Agenda
  calendar_event: { phase: 0, widget: 'agenda', tab: 'overview' },
  // Members -> Phase 0, Initial Response
  member: { phase: 0, widget: 'initialResponse', tab: 'incident-roster' },
};

const DEFAULT_ROUTING: RoutingConfig = {
  phase: 1,
  widget: 'incidentObjectives',
  tab: 'overview',
};

interface BuildEntityUrlOptions {
  incidentId: string;
  entityType: string;
  entityId: string;
  /**
   * Whether the incident has been escalated into Planning P. Not derivable from
   * the operational period number: an escalated incident stays on period 0
   * until its first planning cycle completes.
   */
  isEscalated?: boolean;
}

interface EntityUrlResult {
  url: string;
  isDirect: boolean;
  description: string;
}

export interface EntityRoutingConfig {
  initialResponseTab: string;
  escalatedPhase: number;
  escalatedWidget: string;
}

/** Get the complete routing configuration for an entity type */
export function getEntityRoutingConfig(
  entityType: string
): EntityRoutingConfig {
  const config = ENTITY_ROUTING[entityType] || DEFAULT_ROUTING;
  return {
    initialResponseTab: config.tab,
    escalatedPhase: config.phase,
    escalatedWidget: config.widget,
  };
}

/** Get the initial response tab key for an entity type */
export function getInitialResponseTab(entityType: string): string {
  return (ENTITY_ROUTING[entityType] || DEFAULT_ROUTING).tab;
}

/** Build URL to navigate to the appropriate page for viewing an entity */
export function buildEntityUrl(
  options: BuildEntityUrlOptions
): EntityUrlResult {
  const { incidentId, entityType, isEscalated = false } = options;
  const basePath = `/iap/${incidentId}`;

  if (!isEscalated) {
    return {
      url: `${basePath}/dashboard`,
      isDirect: false,
      description: 'View in dashboard',
    };
  }

  const config = ENTITY_ROUTING[entityType] || DEFAULT_ROUTING;
  const urlPhase = config.phase + 1;

  return {
    url: `${basePath}/dashboard?mode=${FUTURE}&phase=${urlPhase}`,
    isDirect: false,
    description: config.widget,
  };
}

export { getEntityDisplayLabel };
export default buildEntityUrl;
